import React from "react"
import {Row, Col, Switch, Button, Checkbox} from "antd"
import {cloneDeep, keyBy} from "lodash"
import {BasePopup} from "components/popup"
import {KlassDropdown} from "components/Select"
import {BaseButton} from "components/buttons"
import {studentStaffContactsServiceV3, userPersonalContactsServiceV3} from "services"
import {Icon} from "components/Icon"
import {getValuesChangeTwoObj, toastError, toastSuccess} from "helpers"
import styles from "./AddContactForm.module.css"
import {withTranslation} from "react-i18next"

type Props = {
    isShow: boolean
    onClose: Function
    data?: any
    t: Function
    staffContactRoles: SelectOption[]
    personalContactRoles: SelectOption[]
    profiles?: any[]
}

type SelectOption = {
    id: number
    name: string
}

type State = {
    oldData: any
    newData: any
    staffContactRoles: SelectOption[]
    personalContactRoles: SelectOption[]
}

class EditContactForm extends React.Component<Props, State> {
    constructor(props) {
        super(props)
        const {data, staffContactRoles, personalContactRoles} = props
        const initData = this.initData(data)
        this.state = {
            newData: cloneDeep(initData),
            oldData: cloneDeep(initData),
            staffContactRoles: staffContactRoles,
            personalContactRoles: personalContactRoles
        }
    }

    initData = (dataProps) => {
        const result = {
            relationRole:
                dataProps.type === "personal"
                    ? dataProps.userPersonalContactRoleId
                    : dataProps.studentStaffContactRoleId,
            isEmergency: dataProps.type === "personal" && dataProps.isEmergency
        }
        if (!dataProps) return result
        return {...result, ...dataProps}
    }

    onChangeData = (key, value) => {
        const {newData} = this.state
        newData[key] = value
        this.setState({newData})
    }

    getDataSubmit = () => {
        const {oldData, newData} = this.state
        return getValuesChangeTwoObj(oldData, newData)
    }

    onSubmit = async () => {
        const submitData = this.getDataSubmit()
        const {data, t} = this.props
        try {
            if (data.type === "personal") {
                submitData["userPersonalContactRoleId"] = submitData["relationRole"]
                submitData["isEmergency"] = submitData["isEmergency"] ? 1 : 0
                submitData["isDefault"] = submitData["isDefault"] ? 1 : 0
                delete submitData["relationRole"]
                await userPersonalContactsServiceV3.update(data.id, submitData)
            }
            if (data.type === "staff") {
                const activeProfile = this.props.profiles.find((profile) => profile.isActive)
                submitData["studentStaffContactRoleId"] = submitData["relationRole"]
                submitData["isDefault"] = submitData["isDefault"] ? 1 : 0
                submitData["profileId"] = activeProfile.id
                delete submitData["relationRole"]
                await studentStaffContactsServiceV3.update(data.studentStaffContactId, submitData)
            }
            this.props.onClose()
            toastSuccess(t("common:message.updateSuccess"))
        } catch (e) {
            toastError(t("common:message.updateFailure"))
        }
    }

    onClickCancelBtn = () => {
        const {oldData} = this.state
        this.setState({newData: cloneDeep(oldData)})
    }

    getContactRoles = () => {
        const {data} = this.props
        const {staffContactRoles, personalContactRoles} = this.state
        return data.type === "personal" ? personalContactRoles : staffContactRoles
    }

    render() {
        const {newData} = this.state
        const {isShow, onClose, data, t} = this.props
        const roles = this.getContactRoles()
        let isChanged = true
        if (data && data.id) {
            isChanged = !!this.getDataSubmit()
        }
        const rolesKeyById = keyBy(roles, "id")
        const roleValue = rolesKeyById[newData.relationRole]

        return (
            <BasePopup isShow={isShow} onClose={onClose} leftIcon="PERSON_FILL" leftIconColor="#fff" width="880px">
                <p className={styles.title}>{t("user:contact.detail")}</p>
                <div className={styles.bodyWrap}>
                    <p className={styles.fullname}>
                        {data.contactProfile.firstName} {data.contactProfile.lastName}
                    </p>
                    <div className={styles.editContactBody}>
                        <div className={styles.rightInfo}>
                            <div className={styles.rightInfoItem}>
                                <Icon icon="PHONE" className={styles.rightInfoItem__icon} />
                                <p className={styles.rightInfoItem__value}>{data.contactProfile.phone}</p>
                            </div>
                            <div className={styles.rightInfoItem}>
                                <Icon className={styles.rightInfoItem__icon} icon="EMAIL" />
                                <p className={styles.rightInfoItem__value}>{data.contactProfile.email}</p>
                            </div>
                        </div>
                        <div className={styles.rightInfoItem}>
                            <Icon className={styles.rightInfoItem__icon} icon="LOCATION_FILL" />
                            <p className={styles.rightInfoItem__value}>{data.contactProfile.address}</p>
                        </div>
                    </div>
                </div>
                <Row>
                    <Col span={12}>
                        <div>
                            <p className={styles.bodyItemTitle}>{t("user:contact.relationship")}</p>
                            <Checkbox
                                checked={newData.isDefault}
                                onChange={({target: {checked}}) => this.onChangeData("isDefault", checked)}
                                className={styles.checkboxItemRight}>
                                {t("user:contact.makeDefault")}
                            </Checkbox>
                            <div>
                                <KlassDropdown
                                    onChange={(newValue) => this.onChangeData("relationRole", newValue.id)}
                                    options={roles}
                                    placeholder="Select"
                                    value={roleValue}
                                />
                            </div>
                        </div>
                    </Col>
                    {data.type === "personal" && (
                        <Col span={12}>
                            <div className={styles.emergencyWrap}>
                                <p className={styles.bodyItemTitle}>{t("common:message.emergencyContact")}</p>
                                <div className={styles.emergencyBodyWrap}>
                                    <p className={styles.emergencyText}>No</p>
                                    <Switch
                                        checked={newData.isEmergency}
                                        onChange={(newValue) => this.onChangeData("isEmergency", newValue)}
                                    />
                                    <p className={styles.emergencyTextYes}>{t("common:message.yes")}</p>
                                </div>
                            </div>
                        </Col>
                    )}
                </Row>
                <div className={styles.actionWrap}>
                    {data && data.id && isChanged && (
                        <Button className={styles.cancelBtn} onClick={this.onClickCancelBtn}>
                            {t("common:action.cancel")}
                        </Button>
                    )}
                    <div className={styles.addBtnWrap}>
                        <BaseButton title={t("common:action.save")} onClick={this.onSubmit} className={styles.addBtn} />
                    </div>
                </div>
            </BasePopup>
        )
    }
}
export default withTranslation(["user", "common"])(EditContactForm)
